import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, CheckCircle } from "lucide-react";

function FutureSection() {
  const navigate = useNavigate();

  return (
    <div className="px-4 md:px-8 py-16 max-w-7xl mx-auto">
      <motion.div 
        className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-lg"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-[10%] -right-[5%] w-[40%] h-[40%] rounded-full bg-indigo-50 filter blur-[100px] opacity-70"></div>
          <div className="absolute top-[60%] -left-[5%] w-[30%] h-[30%] rounded-full bg-purple-50 filter blur-[100px] opacity-70"></div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="url(#paint0_linear)" d="M40.9,-68.1C52.3,-62.1,60.5,-48.5,64.5,-34.6C68.6,-20.7,68.5,-6.3,65,6.2C61.5,18.7,54.6,29.3,45.8,39.2C37,49.1,26.2,58.3,12.9,63.8C-0.4,69.3,-16.2,71.1,-30.5,66.8C-44.8,62.5,-57.6,52.1,-64.5,38.8C-71.4,25.5,-72.3,9.1,-71.2,-7.3C-70.1,-23.7,-67,-40.3,-57.2,-48.8C-47.5,-57.3,-31.1,-57.8,-16.8,-61.2C-2.5,-64.7,9.8,-70.9,23,-72.2C36.2,-73.6,50.3,-70.1,60.5,-62C70.7,-53.8,77,-40.9,40.9,-68.1Z" transform="translate(100 100)" />
            <defs>
              <linearGradient id="paint0_linear" x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse">
                <stop stopColor="#4F46E5" />
                <stop offset="1" stopColor="#9333EA" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        
        <div className="absolute bottom-0 left-0 w-48 h-48 opacity-10">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="url(#paint1_linear)" d="M42.7,-71.1C53.9,-64.6,60.9,-49.5,67,-34.7C73.1,-19.9,78.4,-5.5,76.3,7.8C74.3,21.1,65,33.2,54.3,42.8C43.6,52.4,31.6,59.4,18.1,65.2C4.6,71,-10.3,75.7,-24.9,73.6C-39.5,71.5,-53.8,62.6,-63.1,50C-72.5,37.5,-76.8,21.2,-76.3,5.3C-75.8,-10.6,-70.4,-26.1,-61.3,-37.9C-52.3,-49.7,-39.6,-57.8,-26.4,-63.5C-13.2,-69.2,0.6,-72.4,15.6,-73.9C30.7,-75.4,47,-83.2,47.9,-80.6C48.8,-78.1,31.4,-77.6,42.7,-71.1Z" transform="translate(100 100)" />
            <defs>
              <linearGradient id="paint1_linear" x1="0" y1="0" x2="200" y2="200" gradientUnits="userSpaceOnUse">
                <stop stopColor="#9333EA" />
                <stop offset="1" stopColor="#4F46E5" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative z-10 px-6 md:px-16 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-10">
          {/* Left Content */}
          <div className="md:w-3/5">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-50 border border-indigo-100 mb-6">
              <div className="w-2 h-2 rounded-full bg-indigo-500 mr-2 animate-pulse"></div>
              <span className="text-indigo-600 text-sm font-medium">Join the movement</span>
            </div>
            
            {/* Main Heading */}
            <motion.h1 
              className="text-4xl md:text-5xl font-bold leading-tight text-gray-800 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              The Future of Innovation <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Starts Here</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p 
              className="text-gray-600 text-lg leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
            >
              Join CelystikHub to create, contribute, and invest in frontier innovation. 
              Leverage our trustless collaboration infrastructure to build products, grow communities, 
              and earn ownership — all backed by blockchain-based knowledge and impact tokenization.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
            >
              <button 
                onClick={() => navigate('/projects')}
                className="px-12 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-indigo-200 transition duration-300 focus:outline-none transform hover:-translate-y-1"
              >
                <div className="flex items-center">
                  <span>Explore Projects</span>
                  <ArrowRight size={18} className="ml-2" />
                </div>
              </button>
              
          
            </motion.div>
          </div>

          {/* Right Content - Feature Highlights */}
          <div className="md:w-2/5">
            <motion.div 
              className="bg-white backdrop-blur-sm rounded-2xl border border-indigo-100 p-6 shadow-lg"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.7 }}
            >
              <h3 className="text-xl font-bold text-indigo-600 mb-4">Why Join CelystikHub?</h3>
              <ul className="space-y-4">
                {[
                  { title: "Ownership", desc: "Gain tokenized ownership of your contributions" },
                  { title: "Collaboration", desc: "Connect with like-minded innovators globally" },
                  { title: "Recognition", desc: "Build your reputation through verifiable impact" },
                  { title: "Rewards", desc: "Earn based on the true value of your contributions" }
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + (index * 0.1), duration: 0.5 }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mr-3 shadow-md">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default FutureSection;
